/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Close, Save } from "@mui/icons-material";
import {
  Card,
  CardContent,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  useMediaQuery,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import { Form, FormikProvider, useFormik } from "formik";
import * as yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import { useSnackbar } from "notistack";
import PageContainer from "@jumbo/components/PageContainer";
import LoadingButton from "@jumbo/components/JumboLoadingButton";
import moment from "moment";
import { KNITTER_ORDER } from "app/routes/constant";
import JumboAutoComplete from "@jumbo/components/JumboAutoComplete";
import {
  createKnitterOrder,
  getByIdKnitterOrder,
  updateKnitterOrder,
} from "app/redux/slice/knitterOrderSlice";
import { getOrderNo } from "app/redux/slice/invoiceSlice";
import { getSupplierList } from "app/redux/slice/supplierSlice";
import { getProductList } from "app/redux/slice/productSlice";
import { istDateInput, istToday } from "app/helpers/istDate";

const useStyles = makeStyles({
  headingContainer: {
    display: "flex",
    justifyContent: "center",
    marginBottom: "20px",
  },
  mainInfoContainer: {
    display: "flex",
    minHeight: "170px",
    justifyContent: "center",
    flexDirection: "column",
  },
  headingTitleContainer: {
    display: "flex",
    flexGrow: "wrap",
  },
  headingTitle: {
    minWidth: "80px",
  },
  headingContent: {
    fontWeight: "500",
  },
  qrContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  table: {
    minWidth: 650,
    "& .MuiTableCell-root": {
      borderLeft: "1px solid",
      borderBottom: "1px solid",
    },
  },
  particular: {
    minWidth: "300px",
  },
  variants: {
    minWidth: "300px",
  },
  amount: {
    minWidth: "100px",
    verticalAlign: "top",
  },
  buttonContainer: {
    display: "flex",
    justifyContent: "end",
    margin: "10px 0px",
  },
  margin: {
    "& > :first-child": {
      marginTop: "10px",
    },
    marginBottom: "10px",
  },
  alignTop: {
    verticalAlign: "top",
  },
  loadingButtonContainer: {
    display: "flex",
    justifyContent: "end",
    marginTop: "20px",
  },
});

const validationSchema = yup.object({
  supplierId: yup.object().required("Please select vendor").nullable(),
  orderNo: yup.string().required("Please enter order number."),
  partyChallan: yup
    .number()
    .typeError("party challan must be a number")
    .required("Please enter party challan.")
    .positive("Party challan must be a positive number"),
  orderDate: yup.string().required("Please enter date."),
  expectedDeliveryDate: yup.string().required("Please enter expiry date."),
  productId: yup.object().required("Please select product").nullable(),
  quantity: yup
    .number()
    .typeError("Quantity must be a number")
    .required("Please enter number of role")
    .min(1),
  unitPrice: yup
    .number()
    .typeError("Unit price must be a number")
    .required("Please enter rate per KG")
    .min(1),
});

const AddAndUpdateKnitterOrder = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const classes = useStyles();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();

  const supplierData = useSelector((state) => state?.supplier);
  const productData = useSelector((state) => state?.product);
  const [isUpdate, setIsUpdate] = useState(false);
  const mobileView = useMediaQuery("(max-width:575px)");

  const formik = useFormik({
    initialValues: {
      supplierId: null,
      orderNo: "",
      partyChallan: "",
      orderDate: istToday(),
      expectedDeliveryDate: istToday(),
      productId: null,
      quantity: "",
      unitPrice: "",
    },
    validationSchema,
    validateOnChange: false,
    onSubmit: async (values, { resetForm, setFieldError, setSubmitting }) => {
      setSubmitting(true);
      const newOrder = {
        supplierId: values?.supplierId?._id,
        productId: values?.productId?._id,
        orderNo: values?.orderNo,
        partyChallan: values?.partyChallan,
        orderDate: values?.orderDate,
        expectedDeliveryDate: values?.expectedDeliveryDate,
        quantity: values?.quantity,
        unitPrice: values?.unitPrice,
      };
      const response = await dispatch(
        id
          ? updateKnitterOrder({ id, data: newOrder })
          : createKnitterOrder(newOrder),
      ).unwrap();
      if (response.status === 200) {
        enqueueSnackbar(response.data?.responseMessage, {
          variant: "success",
        });
        navigate(KNITTER_ORDER);
        resetForm();
      } else {
        enqueueSnackbar(response.data?.responseMessage, {
          variant: "error",
        });
      }
      setSubmitting(false);
    },
  });

  const { values, setFieldValue, errors } = formik;

  useEffect(() => {
    if (id) {
      dispatch(getByIdKnitterOrder(id))
        .unwrap()
        .then((response) => {
          if (response.status === 200) {
            const knitterOrderList = response?.data?.data?.knitterOrderList;
            setFieldValue("supplierId", knitterOrderList?.supplierId);
            setFieldValue("productId", knitterOrderList?.productId);
            setFieldValue("orderNo", knitterOrderList?.orderNo);
            setFieldValue("partyChallan", knitterOrderList?.partyChallan);
            setFieldValue(
              "orderDate",
              istDateInput(knitterOrderList?.orderDate),
            );
            setFieldValue(
              "expectedDeliveryDate",
              moment(knitterOrderList?.expectedDeliveryDate).format(
                "YYYY-MM-DD",
              ),
            );
            setFieldValue("quantity", knitterOrderList?.quantity);
            setFieldValue("unitPrice", knitterOrderList?.unitPrice);
            setIsUpdate(
              knitterOrderList.status === "Complete" ||
                knitterOrderList.status === "Partial"
                ? false
                : true,
            );
          } else {
            enqueueSnackbar(response.data?.responseMessage, {
              variant: "error",
            });
          }
        });
    }
  }, [dispatch, enqueueSnackbar, id, setFieldValue]);

  useEffect(() => {
    if (!id) {
      dispatch(getOrderNo({ type: "knitterOrder" }))
        .unwrap()
        .then((response) => {
          if (response.status === 200) {
            const newOrderNumber = response?.data?.data?.orderNo;
            setFieldValue("orderNo", newOrderNumber);
          } else {
            enqueueSnackbar(response.data?.responseMessage, {
              variant: "error",
            });
          }
        });
    }
    dispatch(getProductList({ field: "name", sort: 1 }));
    dispatch(getSupplierList({ sort: 1, field: "name", type: "Knitter" }));
  }, []);

  const { handleSubmit } = formik;
  return (
    <PageContainer heading={id ? "Update Order" : "Add Order"}>
      <Card>
        <CardContent>
          <FormikProvider value={formik}>
            <Form noValidate autoComplete="off" onSubmit={handleSubmit}>
              <Grid container border={1}>
                <Grid item sm={4} xs={12} borderRight={{ xs: 0, sm: 1 }}>
                  <Grid>
                    <Grid px={1} pt={1} mb={1}>
                      <JumboAutoComplete
                        options={supplierData.list ?? []}
                        label="Vendor*"
                        size="small"
                        getOptionLabel={(option) => option?.name}
                        name={"supplierId"}
                        value={values?.supplierId}
                        onChange={(evt, value) => {
                          setFieldValue("supplierId", value);
                        }}
                        helperText={formik?.errors?.supplierId}
                      />
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item sm={8} xs={12}>
                  <Grid container>
                    <Grid
                      item
                      sm={3}
                      xs={12}
                      borderRight={{ xs: 0, sm: 1 }}
                      p={1}
                    >
                      <TextField
                        label={"Order No."}
                        name="orderNo"
                        fullWidth
                        size="small"
                        value={values?.orderNo}
                        onChange={(evt) =>
                          setFieldValue("orderNo", evt.target.value)
                        }
                        error={errors?.orderNo}
                        helperText={errors?.orderNo}
                      />
                    </Grid>
                    <Grid
                      item
                      sm={3}
                      xs={12}
                      borderRight={{ xs: 0, sm: 1 }}
                      p={1}
                    >
                      <TextField
                        label={"Party Challan"}
                        name="partyChallan"
                        type="text"
                        fullWidth
                        size="small"
                        value={values?.partyChallan}
                        onChange={(evt) =>
                          setFieldValue("partyChallan", evt.target.value)
                        }
                        error={errors?.partyChallan}
                        helperText={errors?.partyChallan}
                      />
                    </Grid>
                    <Grid
                      item
                      sm={3}
                      xs={12}
                      borderRight={{ xs: 0, sm: 1 }}
                      p={1}
                    >
                      <TextField
                        label={"Date"}
                        name="orderDate"
                        fullWidth
                        size="small"
                        type="date"
                        onChange={(evt) =>
                          setFieldValue("orderDate", evt.target.value)
                        }
                        InputLabelProps={{ shrink: true }}
                        value={values?.orderDate}
                        error={errors?.orderDate}
                        helperText={errors?.orderDate}
                      />
                    </Grid>
                    <Grid item sm={3} xs={12} p={1}>
                      <TextField
                        label={"Exp Delivery"}
                        name="expectedDeliveryDate"
                        fullWidth
                        size="small"
                        type="date"
                        onChange={(evt) =>
                          setFieldValue(
                            "expectedDeliveryDate",
                            evt.target.value,
                          )
                        }
                        InputLabelProps={{ shrink: true }}
                        value={values?.expectedDeliveryDate}
                        error={errors?.expectedDeliveryDate}
                        helperText={errors?.expectedDeliveryDate}
                      />
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
              <Grid
                item
                xs={12}
                borderRight={1}
                borderBottom={1}
                sx={{
                  mt: { xs: 3, md: 0 },
                }}
                className="mobile-view-table"
              >
                <TableContainer component={Paper}>
                  <Table
                    sx={{ minWidth: 650 }}
                    size="small"
                    aria-label="a dense table"
                    className={classes?.table}
                  >
                    <TableHead>
                      <TableRow>
                        <TableCell>Sr. No.</TableCell>
                        <TableCell className={classes?.particular}>
                          Product
                        </TableCell>
                        <TableCell>Number Of Role</TableCell>
                        <TableCell>Rate Per-Kg</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        {!mobileView ? (
                          <TableCell sx={{ width: "10px" }}>1</TableCell>
                        ) : (
                          ""
                        )}
                        <TableCell className={classes?.particular}>
                          <JumboAutoComplete
                            options={productData.list ?? []}
                            label="Product*"
                            size="small"
                            getOptionLabel={(option) => option?.name}
                            name={`productId`}
                            value={values?.productId}
                            onChange={(evt, value) => {
                              setFieldValue("productId", value);
                            }}
                            helperText={formik?.errors?.productId}
                          />
                        </TableCell>
                        <TableCell className={classes?.alignTop}>
                          <TextField
                            label="Num Of Roles"
                            placeholder="Num Of Roles"
                            name={`quantity`}
                            type="text"
                            fullWidth
                            value={values?.quantity}
                            size="small"
                            onChange={(evt) =>
                              setFieldValue("quantity", +evt.target.value)
                            }
                            error={errors?.quantity}
                            helperText={errors?.quantity}
                          />
                        </TableCell>
                        <TableCell className={classes?.alignTop}>
                          <TextField
                            label="Rate Per Kg"
                            placeholder="Rate Per Kg"
                            name={`unitPrice`}
                            fullWidth
                            type="text"
                            value={values?.unitPrice}
                            size="small"
                            onChange={(evt) =>
                              setFieldValue("unitPrice", +evt.target.value)
                            }
                            error={errors?.unitPrice}
                            helperText={errors?.unitPrice}
                          />
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>
              <br></br>
              <Grid container spacing={3}>
                <Grid
                  item
                  sm={12}
                  xs={12}
                  className={classes?.loadingButtonContainer}
                >
                  <LoadingButton
                    disabled={id && !isUpdate}
                    type="submit"
                    buttonLabel={id ? "Update" : "Save"}
                    sx={{ marginRight: "10px" }}
                    startIcon={<Save />}
                    variant="outlined"
                    fullWidth={mobileView}
                  />
                  <LoadingButton
                    color={"secondary"}
                    buttonLabel={"Close"}
                    variant="outlined"
                    startIcon={<Close />}
                    onClick={() => navigate(KNITTER_ORDER)}
                    fullWidth={mobileView}
                  />
                </Grid>
              </Grid>
            </Form>
          </FormikProvider>
        </CardContent>
      </Card>
    </PageContainer>
  );
};

export default AddAndUpdateKnitterOrder;
